/**
 * Central forecast-run reducer — pure, useReducer-shaped. The single source of
 * truth for runs + conversations.
 */
import {
  EMPTY_FORECAST,
  applyUpdate,
  isUnknownUpdate,
  type AnyProgressUpdate,
  type UnknownUpdate,
} from "@sooth-labs/idl";
import { DETAIL_FIELDS, EMPTY_RESEARCH, STATUS, isTerminal } from "./model.js";
import { foldResearch } from "./deepProgress.js";
import { mergeReportPointers, unionReportPointers } from "./reportPointerMerge.js";
import {
  isSettled,
  isVoided,
  rollbackPostResolutionVintage,
  withoutPostResolutionRuns,
} from "./postResolutionRuns.js";
import type {
  Contract,
  Entry,
  ForecastEvent,
  NarrationBeat,
  NotifyPrefs,
  Run,
  RunObs,
  ServerContext,
} from "./model.js";
import { DRAFT_CONV_ID } from "./draft.js";
import { headlineOf } from "./progressView.js";
import { isSeedCall } from "./seed.js";

/**
 * The seed's headline, or null if the forecast frame was not coherent enough
 * to read one from.
 *
 * `headlineOf` reaches into the forecast variant's own fields and throws on a
 * shape that folded but does not hold up — a `pmf` whose `mass` is absent or
 * not an array, say. That is survivable in a render path, but this runs inside
 * the reducer, where a throw takes the whole store down with it. A seed with no
 * readable number simply does not get its chat beat.
 */
const seedHeadline = (doc: Parameters<typeof headlineOf>[0]) => {
  try {
    return headlineOf(doc);
  } catch {
    return null;
  }
};

const MAX_RETAINED_PROGRESS_ENTRIES = 100;
// Narration is capped separately, and far higher, than the progress activity
// array above. These beats ARE the chat messages, so a cap that bites deletes
// visible history mid-run.
const MAX_RETAINED_NARRATION_BEATS = 500;

// `as const` (type-only, no runtime effect) narrows each value to its string
// literal, so the action-creator return types below form a real discriminated
// union on `type` — which is what lets `reducer`'s switch narrow each case.
export const ACTIONS = Object.freeze({
  RUN_CREATED: "RUN_CREATED",
  CONTRACT_SET: "CONTRACT_SET",
  CONTRACT_KEY_SET: "CONTRACT_KEY_SET",
  SHARES_CHANGED: "SHARES_CHANGED",
  RUN_STARTED: "RUN_STARTED",
  TRACE_ALLOCATED: "TRACE_ALLOCATED",
  TRACE_REALLOCATED: "TRACE_REALLOCATED",
  RUN_PROGRESS: "RUN_PROGRESS",
  RUN_UPDATE_APPLIED: "RUN_UPDATE_APPLIED",
  RUN_NARRATION_APPENDED: "RUN_NARRATION_APPENDED",
  RUN_COMPLETED: "RUN_COMPLETED",
  RUN_ERRORED: "RUN_ERRORED",
  RUN_CANCELLED: "RUN_CANCELLED",
  RUN_REMOVED: "RUN_REMOVED",
  SCHEDULE_SET: "SCHEDULE_SET",
  SAYER_COPY_ADOPTED: "SAYER_COPY_ADOPTED",
  NOTIFY_PATCHED: "NOTIFY_PATCHED",
  SERIES_SET: "SERIES_SET",
  RESOLUTION_SET: "RESOLUTION_SET",
  PREMISE_CLEARED: "PREMISE_CLEARED",
  PREMISE_OUTCOMES_MARKED: "PREMISE_OUTCOMES_MARKED",
  BRANCH_ARCHIVED: "BRANCH_ARCHIVED",
  CONTRACT_RESOLVED: "CONTRACT_RESOLVED",
  ALERT_SET: "ALERT_SET",
  BRANCH_COLLAPSE_TOGGLED: "BRANCH_COLLAPSE_TOGGLED",
  BRANCH_LINK_FOCUSED: "BRANCH_LINK_FOCUSED",
  SERIES_IMPACTS_SET: "SERIES_IMPACTS_SET",
  FORK_EVENT_SET: "FORK_EVENT_SET",
  RUN_REFORECASTED: "RUN_REFORECASTED",
  RUN_POINTERS_MERGED: "RUN_POINTERS_MERGED",
  DERIVED_TRIGGERS_ATTACHED: "DERIVED_TRIGGERS_ATTACHED",
  ENTRY_APPENDED: "ENTRY_APPENDED",
  ENTRIES_LOADED: "ENTRIES_LOADED",
  DRAFT_ADOPTED: "DRAFT_ADOPTED",
  ACTIVE_SET: "ACTIVE_SET",
  COMPARISON_SET: "COMPARISON_SET",
  COMPARISON_CLEARED: "COMPARISON_CLEARED",
  HYDRATED: "HYDRATED",
  DETAIL_HYDRATED: "DETAIL_HYDRATED",
  DETAIL_REFRESHED: "DETAIL_REFRESHED",
  DELETE_REQUESTED: "DELETE_REQUESTED",
  DELETE_ACKED: "DELETE_ACKED",
  SHARED_HYDRATED: "SHARED_HYDRATED",
  SHARED_RESET: "SHARED_RESET",
  SHARED_ACCESS_LOST: "SHARED_ACCESS_LOST",
  CUSTOM_EVENT_POPOVER_OPENED: "CUSTOM_EVENT_POPOVER_OPENED",
  CUSTOM_EVENT_POPOVER_EDITED: "CUSTOM_EVENT_POPOVER_EDITED",
  CUSTOM_EVENT_POPOVER_REJECTED: "CUSTOM_EVENT_POPOVER_REJECTED",
  CUSTOM_EVENT_POPOVER_CLOSED: "CUSTOM_EVENT_POPOVER_CLOSED",
} as const);

export interface Conversation {
  entries: Entry[];
}

/**
 * The "add custom event" popover of one forecast. It belongs to that forecast's run, never to
 * the app: it shows only while its run is selected, survives a switch away and back, and its
 * Branch button branches that run whatever is selected when a late rejection reopens it.
 */
export interface CustomEventPopover {
  screenX: number;
  screenY: number;
  /** The placement day (YYYY-MM-DD) the date picker holds. */
  date: string;
  /** The placement instant, when a click or a chat trigger gave one. */
  dateTime?: string | null;
  /** The typed text, kept so it survives the popover being hidden by a selection change. */
  description?: string;
  /** Why the last attempt from this popover made no branch. */
  problem?: string | null;
  /** The depth picked for the scenario; Quick when unset. */
  tier?: "quick" | "deep";
}

export interface RunState {
  runsById: Record<string, Run>;
  conversationsById: Record<string, Conversation>;
  activeRunId: string | null;
  comparison: { rootIds: string[] };
  // Ids awaiting the persisted `DELETE /api/forecasts/:id` cascade. `deleteRequested` adds
  // them alongside `runRemoved`; the persister's store subscription drains them and
  // dispatches `deleteAcked` once the DELETE fires.
  pendingDeleteIds: string[];
  /**
   * Shared-with-me rows, hydrated from `GET /api/forecasts/shared` — a DELIBERATELY
   * SEPARATE slice from `runsById`, never merged in. Root-ownership walks (`rootOf`)
   * assume every member of `runsById` is the caller's own, so merging a foreign row in
   * would put it into those walks. Every row here has `isOwn: false` (see `Run`'s own
   * doc) by construction — nothing sets it any other way.
   *
   * A shared row does have a conversation. Conversations are keyed by `conversationId`
   * in a slice of their own, so they are not tied to which run map a run lives in;
   * selectors that walk from a possibly-shared runId resolve through `_sliceFor`/`selectRun`.
   */
  sharedRunsById: Record<string, Run>;
  /**
   * Each run's open custom-event popover, keyed by runId. UI state only: never persisted and
   * never hydrated (no server row carries it), and dropped with its run. Kept beside the runs
   * rather than on them so typing into a popover does not change its run's identity.
   */
  customEventPopovers: Record<string, CustomEventPopover>;
  /**
   * The run that adopted each draft message, by its `msg_id`. Kept after that run is removed:
   * a reply to an adopted question must be dropped then, not filed back in the draft.
   */
  adoptedDraftMsgs: Record<string, string>;
  /**
   * The branch a `?forecast=` link named, for the forecast view to bring into view and highlight
   * once (SOO-1008). `seq` grows with each request, so the view acts on each one once. UI state
   * only: never persisted or hydrated; absent until a link names a branch.
   */
  branchLinkFocus?: { runId: string; seq: number };
}

export const initialState: RunState = Object.freeze({
  runsById: {},
  conversationsById: {},
  activeRunId: null,
  comparison: { rootIds: [] },
  pendingDeleteIds: [],
  sharedRunsById: {},
  customEventPopovers: {},
  adoptedDraftMsgs: {},
});

function boundedResultHeadline(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1
    ? value
    : null;
}

interface TracePayload {
  traceId?: string | null;
  forecastId?: string | null;
  backend?: string | null;
}

export const runCreated = (run: Run) => ({ type: ACTIONS.RUN_CREATED, run });
export const contractSet = (runId: string, contract: Contract) => ({
  type: ACTIONS.CONTRACT_SET,
  runId,
  contract,
});
// Dispatched after a successful share save. Without it the SHARE control keeps
// its pre-save label until a reload: `isShared` arrives on the LIST fetch, and
// nothing re-fetches that list when the dialog saves. Carries a boolean the
// caller already computed from the submitted set, so no round trip is needed to
// learn what we just wrote.
export const sharesChanged = (runId: string, isShared: boolean) => ({
  type: ACTIONS.SHARES_CHANGED,
  runId,
  isShared,
});
export const contractKeySet = (
  runId: string,
  contractKey: string | null,
  contractKeyStatus: string | null = null,
) => ({
  type: ACTIONS.CONTRACT_KEY_SET,
  runId,
  contractKey,
  contractKeyStatus,
});
// `reforecastAt` (optional): the moment a MANUAL reforecast begins. A reforecast
// reuses the original run (old createdAt), so stamping lastReforecastedAt at start
// lets the chat projection sort the live progress line to the bottom (below the
// original forecast's report summary) instead of at the stale createdAt position.
// Fresh runs omit it → createdAt drives ordering, unchanged.
export const runStarted = (runId: string, reforecastAt: string | null = null) => ({
  type: ACTIONS.RUN_STARTED,
  runId,
  reforecastAt,
  startedAt: reforecastAt ? new Date().toISOString() : null,
});
export const traceAllocated = (
  runId: string,
  { traceId = null, forecastId = null, backend = null }: TracePayload = {},
) => ({ type: ACTIONS.TRACE_ALLOCATED, runId, traceId, forecastId, backend });
// Re-stampable trace update for REFORECAST: a reforecast reruns an existing run
// whose traceId is already set, so the write-once traceAllocated/stampRun would
// ignore the new server trace, leaving the store trace stale vs the server's
// rolled-forward lifecycle row. This overwrites via patchRun.
export const traceReallocated = (
  runId: string,
  { traceId = null, forecastId = null, backend = null }: TracePayload = {},
) => ({ type: ACTIONS.TRACE_REALLOCATED, runId, traceId, forecastId, backend });
// message is the human-readable stage label.
// Unified shape: {stage, step, pct, message} — matches progressFromRow (hydrate.js) so live
// progress and rehydrated progress are identical shapes (no undefined fields on either path).
// pct is 0-100; a fraction is not accepted.
export const runProgress = (
  runId: string,
  {
    stage,
    step = null,
    stepIdx = -1,
    pct,
    message = null,
  }: {
    stage: string | null;
    step?: string | null;
    stepIdx?: number;
    pct: number | null;
    message?: string | null;
  },
) => ({
  type: ACTIONS.RUN_PROGRESS,
  runId,
  stage,
  step,
  stepIdx,
  pct,
  message,
});
export const runUpdateApplied = (runId: string, update: AnyProgressUpdate | UnknownUpdate) => ({
  type: ACTIONS.RUN_UPDATE_APPLIED,
  runId,
  update,
});
export const runNarrationAppended = (runId: string, beat: NarrationBeat) => ({
  type: ACTIONS.RUN_NARRATION_APPENDED,
  runId,
  beat,
});

/** Optional auxiliary fields are applied only when `!== undefined`: omitted leaves the run
 *  unchanged and an explicit `null` clears. The reforecast payload follows the same rule. */
interface RunResultPayload {
  contract?: Contract | null;
  events?: ForecastEvent[] | null;
  serverContext?: ServerContext | null;
  serverPrediction?: number | null;
  forecastId?: string | null;
  partial?: boolean;
  debug?: unknown;
  obs?: RunObs | null;
  serverExplanation?: unknown;
  report?: unknown;
  // Restores the cooldown stamp when a run that optimistically entered RUNNING turns out
  // not to be a reforecast after all (reforecast's settled skip). Omitted everywhere else,
  // so a normal completion leaves whatever RUN_STARTED stamped untouched.
  lastReforecastedAt?: string | null;
}

export const runCompleted = (
  runId: string,
  {
    contract,
    events,
    serverContext,
    serverPrediction,
    forecastId = null,
    partial,
    debug,
    obs,
    serverExplanation,
    report,
    lastReforecastedAt,
  }: RunResultPayload,
) => ({
  type: ACTIONS.RUN_COMPLETED,
  runId,
  contract,
  events,
  serverContext,
  serverPrediction,
  forecastId,
  partial,
  debug,
  obs,
  serverExplanation,
  report,
  lastReforecastedAt,
});
export const runErrored = (runId: string, error: unknown) => ({
  type: ACTIONS.RUN_ERRORED,
  runId,
  error,
});
export const runCancelled = (runId: string) => ({ type: ACTIONS.RUN_CANCELLED, runId });
export const runRemoved = (runId: string) => ({ type: ACTIONS.RUN_REMOVED, runId });
// Deletion is best-effort: `deleteAcked` drains the id once the persister fires the DELETE,
// and the DELETE is never retried beyond that.
export const deleteRequested = (runId: string) => ({ type: ACTIONS.DELETE_REQUESTED, runId });
export const deleteAcked = (runId: string) => ({ type: ACTIONS.DELETE_ACKED, runId });

export const scheduleSet = (runId: string, schedule: unknown) => ({
  type: ACTIONS.SCHEDULE_SET,
  runId,
  schedule,
});
/**
 * A copy of one of Sayer's forecasts is now the user's own: drop its Sayer marker and take what
 * the conversion set (its schedule, and the contract key and status it was registered with).
 */
export const sayerCopyAdopted = (
  runId: string,
  converted: { schedule?: unknown; contractKey?: unknown; contractStatus?: unknown } = {},
) => ({
  type: ACTIONS.SAYER_COPY_ADOPTED,
  runId,
  converted,
});
/**
 * Merge an alert-prefs edit into the run's `notify`, mirroring the server's
 * `mergeNotifyPrefs` (keys absent from the patch keep their stored value).
 */
export const notifyPatched = (runId: string, patch: NotifyPrefs) => ({
  type: ACTIONS.NOTIFY_PATCHED,
  runId,
  patch,
});
export const seriesSet = (runId: string, series: Record<string, unknown>) => ({
  type: ACTIONS.SERIES_SET,
  runId,
  series,
});
export const resolutionSet = (runId: string, resolution: unknown) => ({
  type: ACTIONS.RESOLUTION_SET,
  runId,
  resolution,
});
/** Carries the server's updated premise record: a grading (from a realtime re-read), or one
 *  that now holds `clearedAt` — the run KEEPS existing, it just stops being drawn:
 *  `selectVisibleBranchRuns` reads that stamp. */
export const premiseCleared = (runId: string, premise: unknown) => ({
  type: ACTIONS.PREMISE_CLEARED,
  runId,
  premise,
});
/** A run's events with its graded scenarios' triggers marked as they turned out. No status or
 *  result change, so any later save of the run carries the marks grading put on its row. */
export const premiseOutcomesMarked = (runId: string, events: ForecastEvent[]) => ({
  type: ACTIONS.PREMISE_OUTCOMES_MARKED,
  runId,
  events,
});
/** CLEAR BRANCH archived a branch at `clearedAt` (ISO), or `null` to undo an optimistic
 *  archive the server refused. The run KEEPS existing: the live lane hides it and a past
 *  mark from before `clearedAt` still draws it (`selectVisibleBranchRuns`). */
export const branchArchived = (runId: string, clearedAt: string | null) => ({
  type: ACTIONS.BRANCH_ARCHIVED,
  runId,
  clearedAt,
});
// Push-driven resolution pickup: a `contract.resolved` realtime hint re-fetches the row
// and applies the resolved state live. The resolved state has two distinct consumers, so
// this sets both fields in one dispatch (what a reload hydrates together):
// `resolution.status` drives the on-chart verdict visual, while
// `contractStatus === "resolved"` gates the resolution report section and forces the
// cadence pill to None (terminal status). `resolutionSet` alone patches only
// `resolution`, so it can't flip contractStatus.
export const contractResolved = (
  runId: string,
  { contractStatus, resolution }: { contractStatus?: unknown; resolution?: unknown },
) => ({
  type: ACTIONS.CONTRACT_RESOLVED,
  runId,
  contractStatus,
  resolution,
});
export const alertSet = (runId: string, alert: boolean) => ({
  type: ACTIONS.ALERT_SET,
  runId,
  alert,
});
export const branchCollapseToggled = (runId: string) => ({
  type: ACTIONS.BRANCH_COLLAPSE_TOGGLED,
  runId,
});
/** A `?forecast=` link named this branch: expand it and its parent branches, and focus it. */
export const branchLinkFocused = (runId: string) => ({
  type: ACTIONS.BRANCH_LINK_FOCUSED,
  runId,
});
export const seriesImpactsSet = (runId: string, seriesImpacts: Record<string, unknown>) => ({
  type: ACTIONS.SERIES_IMPACTS_SET,
  runId,
  seriesImpacts,
});
// `forkEvent.direction` drives the fork trigger's above/below-spine placement and red/blue
// colour, so a post-completion caller flips a custom-event branch's trigger to "no"
// (red/below) by setting forkEvent here.
export const forkEventSet = (runId: string, forkEvent: ForecastEvent | null) => ({
  type: ACTIONS.FORK_EVENT_SET,
  runId,
  forkEvent,
});
// The custom-event popover of one run (see CustomEventPopover). Opening replaces whatever that
// run's popover held; a rejection reopens it only when that run has none open, so a late
// rejection never replaces a popover the user has since opened on the same run.
export const customEventPopoverOpened = (runId: string, popover: CustomEventPopover) => ({
  type: ACTIONS.CUSTOM_EVENT_POPOVER_OPENED,
  runId,
  popover,
});
export const customEventPopoverEdited = (
  runId: string,
  patch: Pick<Partial<CustomEventPopover>, "description" | "date" | "tier">,
) => ({ type: ACTIONS.CUSTOM_EVENT_POPOVER_EDITED, runId, patch });
export const customEventPopoverRejected = (runId: string, popover: CustomEventPopover) => ({
  type: ACTIONS.CUSTOM_EVENT_POPOVER_REJECTED,
  runId,
  popover,
});
export const customEventPopoverClosed = (runId: string) => ({
  type: ACTIONS.CUSTOM_EVENT_POPOVER_CLOSED,
  runId,
});
// Do NOT default the optional auxiliary fields. Core result fields replace their prior values.
export const runReforecasted = (
  runId: string,
  {
    contract,
    events,
    serverContext,
    serverPrediction,
    lastReforecastedAt,
    partial,
    debug,
    obs,
    serverExplanation,
    report,
    history,
    reportPointers,
    superseded,
  }: RunResultPayload & {
    lastReforecastedAt?: string | null;
    history?: unknown[];
    reportPointers?: unknown[];
    superseded?: unknown[];
  },
) => ({
  type: ACTIONS.RUN_REFORECASTED,
  runId,
  contract,
  events,
  serverContext,
  serverPrediction,
  lastReforecastedAt,
  partial,
  debug,
  obs,
  serverExplanation,
  report,
  history,
  reportPointers,
  superseded,
});

/**
 * Merge a fresh row's report-pointer history onto a run — that and nothing else.
 *
 * Deliberately NOT `runReforecasted`, which forces `status: COMPLETED`. The read-through
 * that carries these normally lands while the run is still RUNNING (the hint is emitted
 * inside the completion tap, before the SSE response closes, whereas the client completes
 * the run only after its post-stream read), so reusing it would finalise a
 * streaming run early.
 */
export const runPointersMerged = (runId: string, reportPointers: unknown[]) => ({
  type: ACTIONS.RUN_POINTERS_MERGED,
  runId,
  reportPointers,
});

/**
 * A derived branch's own generated triggers, read back from the authoritative row
 * (`POST /api/forecasts/:id/branch-triggers`). The guards make a late or repeated response
 * a no-op.
 */
export const derivedTriggersAttached = (
  runId: string,
  forecastId: string,
  events: ForecastEvent[],
) => ({
  type: ACTIONS.DERIVED_TRIGGERS_ATTACHED,
  runId,
  forecastId,
  events,
});

export const entryAppended = (conversationId: string, entry: Entry) => ({
  type: ACTIONS.ENTRY_APPENDED,
  conversationId,
  entry,
});
export const entriesLoaded = (conversationId: string, entries: Entry[]) => ({
  type: ACTIONS.ENTRIES_LOADED,
  conversationId,
  entries,
});
/** A new root run took over the draft conversation: its entries move to the run's thread. */
export const draftAdopted = (runId: string) => ({ type: ACTIONS.DRAFT_ADOPTED, runId });
export const activeSet = (runId: string | null) => ({ type: ACTIONS.ACTIVE_SET, runId });
export const comparisonSet = (rootIds: string[]) => ({ type: ACTIONS.COMPARISON_SET, rootIds });
export const comparisonCleared = () => ({ type: ACTIONS.COMPARISON_CLEARED });
export const hydrated = ({
  runsById,
  conversationsById,
}: {
  runsById: Record<string, Run>;
  conversationsById: Record<string, Conversation>;
}) => ({
  type: ACTIONS.HYDRATED,
  runsById,
  conversationsById,
});
/**
 * Fill the DETAIL fields of already-hydrated runs from the full-view backfill.
 *
 * Boot hydration takes the LEAN list so first paint isn't gated on a multi-MB
 * response; the detail blobs land moments later and are merged with this action.
 * It is deliberately NOT a second `hydrated` dispatch: HYDRATED replaces each run
 * object wholesale, which would clobber anything the user did in the window
 * between the two responses (a run they started, progress frames that arrived, a
 * schedule they toggled). DETAIL_HYDRATED instead patches ONLY the detail keys.
 */
export const detailHydrated = (detailByRunId: Record<string, Partial<Run>>) => ({
  type: ACTIONS.DETAIL_HYDRATED,
  detailByRunId,
});

/**
 * Replace a filled run's detail with a newer saved vintage read by id (an update its owner saved
 * after this tab read it). Unlike DETAIL_HYDRATED it overwrites what the run holds, local
 * vintages included: the caller has checked that the answer is not older than the run's history
 * and that no rerun is in flight. Patch-in-place, never insert.
 */
export const detailRefreshed = (detailByRunId: Record<string, Partial<Run>>) => ({
  type: ACTIONS.DETAIL_REFRESHED,
  detailByRunId,
});

/**
 * ADDITIVE: the shared lane paginates (`GET /api/forecasts/shared`'s `next_page_token`),
 * so each page's rows accumulate rather than overwrite the ones a prior page already
 * loaded.
 * Every `Run` here MUST already carry `isOwn: false` — set by `rowToRun`
 * from the wire row, never by this action.
 */
export const sharedHydrated = (runsById: Record<string, Run>) => ({
  type: ACTIONS.SHARED_HYDRATED,
  runsById,
});

/**
 * Shared hydration accumulates, so a fresh load starts from an empty slice rather than
 * keeping stale rows across sessions/loads.
 */
export const sharedReset = () => ({ type: ACTIONS.SHARED_RESET });

/**
 * Revocation is deliberately not pushed (the owner unticking a box does not
 * reach into anyone's open tab), so a recipient's list can hold a row the server
 * would now refuse. The server's 404 when they act on it is the discovery, and this
 * action is what the discovery does to the list.
 *
 * Removal, not a flag: a row nobody may read has nothing left to render. Leaving
 * it visible-but-marked would keep stale content on screen and invite a second
 * click on the same dead row.
 */
export const sharedAccessLost = (runId: string) => ({
  type: ACTIONS.SHARED_ACCESS_LOST,
  runId,
});

/**
 * The full run-domain action union. The action shapes are defined by the creators above;
 * each member is the `ReturnType` of one of them, not a hand-written shape.
 */
export type RunAction =
  | ReturnType<typeof runCreated>
  | ReturnType<typeof contractSet>
  | ReturnType<typeof contractKeySet>
  | ReturnType<typeof sharesChanged>
  | ReturnType<typeof runStarted>
  | ReturnType<typeof traceAllocated>
  | ReturnType<typeof traceReallocated>
  | ReturnType<typeof runProgress>
  | ReturnType<typeof runUpdateApplied>
  | ReturnType<typeof runNarrationAppended>
  | ReturnType<typeof runCompleted>
  | ReturnType<typeof runErrored>
  | ReturnType<typeof runCancelled>
  | ReturnType<typeof runRemoved>
  | ReturnType<typeof scheduleSet>
  | ReturnType<typeof sayerCopyAdopted>
  | ReturnType<typeof notifyPatched>
  | ReturnType<typeof seriesSet>
  | ReturnType<typeof resolutionSet>
  | ReturnType<typeof premiseCleared>
  | ReturnType<typeof premiseOutcomesMarked>
  | ReturnType<typeof branchArchived>
  | ReturnType<typeof contractResolved>
  | ReturnType<typeof alertSet>
  | ReturnType<typeof branchCollapseToggled>
  | ReturnType<typeof branchLinkFocused>
  | ReturnType<typeof seriesImpactsSet>
  | ReturnType<typeof forkEventSet>
  | ReturnType<typeof runReforecasted>
  | ReturnType<typeof runPointersMerged>
  | ReturnType<typeof derivedTriggersAttached>
  | ReturnType<typeof entryAppended>
  | ReturnType<typeof entriesLoaded>
  | ReturnType<typeof draftAdopted>
  | ReturnType<typeof activeSet>
  | ReturnType<typeof comparisonSet>
  | ReturnType<typeof comparisonCleared>
  | ReturnType<typeof hydrated>
  | ReturnType<typeof detailHydrated>
  | ReturnType<typeof detailRefreshed>
  | ReturnType<typeof deleteRequested>
  | ReturnType<typeof deleteAcked>
  | ReturnType<typeof sharedHydrated>
  | ReturnType<typeof sharedAccessLost>
  | ReturnType<typeof sharedReset>
  | ReturnType<typeof customEventPopoverOpened>
  | ReturnType<typeof customEventPopoverEdited>
  | ReturnType<typeof customEventPopoverRejected>
  | ReturnType<typeof customEventPopoverClosed>;

/**
 * Every patch helper below must resolve the slice through this rather than reading
 * `runsById` directly, or its writes to shared rows become SILENT NO-OPs.
 */
function sliceOf(state: RunState, runId: string): "runsById" | "sharedRunsById" | null {
  if (state.runsById[runId]) return "runsById";
  if (state.sharedRunsById[runId]) return "sharedRunsById";
  return null;
}

/** The popover map without `runId`'s entry; the same map when it has none. */
function withoutPopover(
  popovers: Record<string, CustomEventPopover> | undefined,
  runId: string,
): Record<string, CustomEventPopover> {
  if (!popovers?.[runId]) return popovers ?? {};
  const { [runId]: _closed, ...rest } = popovers;
  return rest;
}

function runOf(state: RunState, runId: string): Run | undefined {
  const key = sliceOf(state, runId);
  return key ? state[key][runId] : undefined;
}

/**
 * A copy of one of Sayer's forecasts runs at the lane it follows. The lean list stamps `backend`
 * once, and the copy can move to another lane before its detail arrives (server
 * sayerForecasts/follow.js), so the backend follows the context's lane; a branch started from the
 * copy inherits it. Other runs, and a copy the user has made their own, are returned unchanged.
 */
function withCopyBackend<R extends { backend?: unknown; serverContext?: unknown }>(run: R): R {
  const ctx: any = run.serverContext;
  const lane = ctx?.origin?.kind === "observer" ? ctx?.metadata?.orchestrator : null;
  return typeof lane === "string" && lane && lane !== run.backend ? { ...run, backend: lane } : run;
}

/**
 * The server context after a newer forecast lands. A copy of one of Sayer's forecasts keeps its
 * `origin` and `metadata` (the lane it follows): the fetched report carries neither, and the
 * server still treats the row as Sayer's, so losing them would unlock its owner-only controls.
 */
function keepSayerCopyContext(live: any, incoming: any): any {
  const origin = live?.origin;
  if (!origin || origin.kind !== "observer") return incoming;
  return {
    ...(incoming && typeof incoming === "object" ? incoming : {}),
    origin,
    ...(live.metadata ? { metadata: live.metadata } : {}),
  };
}

/**
 * Writes back to the slice the run came from: a shared run must not be promoted into
 * `runsById`, which the branch/conversation root-walks assume is owner-only (see
 * RunState.sharedRunsById's doc).
 */
function patchRun(state: RunState, runId: string, patch: Partial<Run>): RunState {
  const key = sliceOf(state, runId);
  if (!key) return state;
  const slice = state[key];
  return { ...state, [key]: { ...slice, [runId]: { ...slice[runId], ...patch } } };
}

function settledRun(run: Run): Run {
  const reportPointers = withoutPostResolutionRuns(run.reportPointers, run);
  const rollback = rollbackPostResolutionVintage(run);
  if (reportPointers === run.reportPointers && !rollback) return run;
  if (!rollback) return { ...run, reportPointers };
  // The restored fields are local now, so a detail backfill (serialized from the row the
  // late run may already have been saved into) must not write them back over.
  const owned = Array.isArray(run.detailLocalFields) ? run.detailLocalFields : [];
  return {
    ...run,
    ...(rollback as Partial<Run>),
    reportPointers,
    detailLocal: true,
    detailLocalFields: [
      ...new Set([...owned, ...localDetailFields(rollback as unknown as Record<string, unknown>)]),
    ],
  };
}

/**
 * Re-apply the post-resolution rule to `runId` after a patch changed its pointers or its
 * resolution. The pointer merge is a union, so this is the only place a run the server has
 * pruned can leave an open tab; a stale snapshot that re-adds it is dropped again here.
 */
function settleRun(state: RunState, runId: string): RunState {
  const key = sliceOf(state, runId);
  if (!key) return state;
  const run = state[key][runId];
  const next = settledRun(run);
  return next === run ? state : { ...state, [key]: { ...state[key], [runId]: next } };
}

function keptDetail(prior: Run): Partial<Run> {
  const kept: Partial<Run> = {
    // The headline and its moment belong to the vintage the detail holds: a promoted vintage
    // would otherwise show the older row's prediction beside its own history.
    serverPrediction: prior.serverPrediction,
    lastReforecastedAt: prior.lastReforecastedAt,
    detailPending: prior.detailPending,
    detailLocal: prior.detailLocal,
    detailLocalFields: prior.detailLocalFields,
  };
  for (const field of DETAIL_FIELDS) (kept as Record<string, unknown>)[field] = prior[field];
  return kept;
}

function staleOverlayKeeps(prior: Run): Partial<Run> {
  return {
    contractStatus: prior.contractStatus,
    resolution: prior.resolution,
    ...keptDetail(prior),
  };
}

/**
 * Overlay incoming runs onto a slice: each incoming run replaces the one it shares a
 * runId with, except that a run already in the slice keeps the union of both pointer
 * lists. A hydrate built from an older response must not be able to shrink a list another
 * path has already grown (see reportPointerMerge).
 */
function overlayRuns(slice: Record<string, Run>, incoming: Record<string, Run>) {
  const next: Record<string, Run> = { ...slice, ...incoming };
  for (const [runId, run] of Object.entries(incoming)) {
    const prior = slice[runId];
    if (!prior) {
      next[runId] = settledRun(run);
      continue;
    }
    // A resolution is final, so a response serialized while the row still read open must
    // not reopen it here: that would un-prune the late run and bring its vintage back. Only
    // a void — a genuine later change, carried as its own status — replaces a settled run's
    // resolution; any other unsettled snapshot keeps the one the store already holds.
    // A void is final too, and it is exactly the case `isSettled` leaves out: an incoming
    // snapshot that is not a void cannot un-void the run, whatever it claims.
    const heldVoid = isVoided(prior) && !isVoided(run);
    // A LEAN response over a settled run that owns locally restored detail is stale too, even
    // carrying the same verdict: it can be serialized after the server stamped the resolution
    // but before this tab's rollback was saved, and it has no detail of its own to show.
    const leanOverRestored =
      !!run.detailPending && isSettled(prior) && !!prior.detailLocal && !isVoided(run);
    const stale =
      heldVoid || leanOverRestored || (isSettled(prior) && !isSettled(run) && !isVoided(run));
    next[runId] = settledRun({
      ...run,
      // A lean snapshot has no detail to give: a run already filled keeps its own (a shared
      // list reloaded after the forecast was opened, say), or it would go back to loading; and a
      // lean run a resume completed here keeps its result and the markers that stop the later
      // backfill overwriting it, while that backfill stays owed.
      ...(run.detailPending && (!prior.detailPending || prior.detailLocal)
        ? keptDetail(prior)
        : {}),
      // Proven older than the resolution the store holds, so its DISPLAY is older too — often
      // the very late vintage the store already stepped back from, and a lean one carries no
      // history to step back again. Keep what is on screen; take only its pointers.
      ...(stale ? staleOverlayKeeps(prior) : {}),
      // Client-only, so no response carries them: an overlay must not drop either.
      superseded: prior.superseded,
      vintageRestored: prior.vintageRestored || run.vintageRestored,
      reportPointers: mergeReportPointers(prior.reportPointers, run.reportPointers),
    });
  }
  return next;
}

/**
 * selectChatProjection renders `run.error` directly as the errored run's chat-line
 * content, which <Linkify> calls .split() on — so a non-string (raw Error object,
 * plain payload) would throw mid-render. Callers pass
 * raw Error instances, `{message}` shapes, and message-less payloads like
 * `{code:'result_unavailable'}` — map each to something readable rather than the
 * "[object Object]" a bare String() would produce; nullish → null (the selector's
 * `run.error || 'Forecast errored'` fallback then applies).
 */
/** The server error code an Error or error payload carries, or null. */
function errorCodeOf(e: any): string | null {
  return e != null && typeof e === "object" && typeof e.code === "string" && e.code ? e.code : null;
}

function errorToString(e: any): string | null {
  if (e == null) return null;
  if (typeof e === "string") return e;
  if (typeof e.message === "string" && e.message) return e.message;
  if (typeof e.code === "string" && e.code) return e.code;
  // An Error with an empty message falls through to its name ("TypeError"), not "{}".
  if (typeof e.name === "string" && e.name) return e.name;
  // JSON.stringify can legally return undefined (functions/symbols, toJSON→undefined),
  // which would drop the error; fall back to String(e) so the result is always a string.
  try {
    return JSON.stringify(e) ?? String(e);
  } catch {
    return String(e);
  }
}

/**
 * Write-once, single-assignment stamp for traceId/forecastId/backend: a later
 * TRACE_ALLOCATED with a conflicting value is ignored (keeps the first), and a
 * stamp that changes nothing returns the same state ref.
 */
function stampRun(state: RunState, runId: string, patch: Partial<Run>): RunState {
  const key = sliceOf(state, runId);
  if (!key) return state;
  const run = state[key][runId];
  // `any` accumulator: this helper stamps dynamic keys (Object.entries loop), so
  // a keyed Run index would need a cast at every access — one local `any` is cleaner.
  const next: any = { ...run };
  let changed = false;
  for (const [k, v] of Object.entries(patch)) {
    if (v != null && next[k] == null) {
      next[k] = v;
      changed = true;
    }
  }
  if (!changed) return state;
  return { ...state, [key]: { ...state[key], [runId]: next } };
}

function localDetailFields(patch: Record<string, unknown>): string[] {
  // `k in patch` is not enough: these actions put `contract`/`events`/`serverContext` in the
  // patch unconditionally, so an action that omits them still records them as owned — and the
  // backfill would then skip filling them, leaving a lean run permanently without a contract.
  // An explicit `null` IS an owned write (the run said "there is none") and must be kept.
  return DETAIL_FIELDS.filter((k) => patch[k] !== undefined);
}

/**
 * The owned set ACCUMULATES across local writes, it does not replace.
 *
 * Two local updates can land before one backfill — a completion, then a reforecast that
 * omits an optional field. Replacing the set would drop the first write's fields from it,
 * and `DETAIL_HYDRATED` would then overlay the stale pre-completion value on data the user
 * had already produced.
 */
function ownedAfter(state: RunState, runId: string, patch: Record<string, unknown>): string[] {
  const prev = runOf(state, runId)?.detailLocalFields;
  const next = localDetailFields(patch);
  return Array.isArray(prev) && prev.length ? [...new Set([...prev, ...next])] : next;
}

export function reducer(state: RunState = initialState, action: RunAction): RunState {
  switch (action.type) {
    case ACTIONS.RUN_CREATED: {
      const { run } = action;
      const conversationsById = state.conversationsById[run.conversationId]
        ? state.conversationsById
        : { ...state.conversationsById, [run.conversationId]: { entries: [] } };
      return { ...state, runsById: { ...state.runsById, [run.runId]: run }, conversationsById };
    }
    case ACTIONS.CONTRACT_SET:
      return patchRun(state, action.runId, { contract: action.contract });
    case ACTIONS.SHARES_CHANGED:
      return patchRun(state, action.runId, { isShared: action.isShared });
    case ACTIONS.CONTRACT_KEY_SET:
      return patchRun(state, action.runId, {
        contractKey: action.contractKey,
        contractKeyStatus: action.contractKeyStatus,
      });
    case ACTIONS.RUN_STARTED:
      return patchRun(state, action.runId, {
        status: STATUS.RUNNING,
        // The start stamp is overwritten by the authoritative value on completion
        // (runReforecasted).
        ...(action.reforecastAt ? { lastReforecastedAt: action.reforecastAt } : {}),
        // The run-status clock uses wall time, independent of the debug timeline date;
        // a resume-reattach (null) keeps the hydrated server value.
        ...(action.reforecastAt ? { startedAt: action.startedAt } : {}),
        // A manual reforecast (reforecastAt set) REUSES the already-completed run, which
        // still carries the prior run's final progress. RUN_PROGRESS is monotonic (never
        // regresses), so without clearing it here every fresh early frame is rejected and
        // the line stays pinned at the last step. Reset it so the ladder climbs from the
        // start again. A resume-reattach passes null, so hydrated progress is preserved.
        ...(action.reforecastAt
          ? {
              progress: null,
              progressDoc: null,
              progressForecastRevisions: 0,
              progressReportRevisions: 0,
              progressSeed: false,
              progressSeedArmed: false,
              progressSeedReportArmed: false,
              progressSeedReportSeen: false,
              progressSeedStarted: false,
              progressSeedOutcome: null,
              progressTailReport: false,
              progressActivity: null,
              // Narration fails the SAME way the progress ladder above does, for
              // the same reason. Its dedupe guard drops any ordinal at or below
              // the newest one held, and a reforecast gets a FRESH narrating
              // source whose ordinals restart at 0 — so stale beats would linger
              // in the chat AND every new beat would be rejected until it
              // out-counted the old run's. Clearing solves both: the previous
              // run's commentary describes work that is no longer happening.
              narration: [],
              progressResearch: EMPTY_RESEARCH,
            }
          : {}),
        // A manual reforecast goes RUNNING at CLICK, before its new server trace exists,
        // so the run still carries the PREVIOUS run's `traceId` for the length of the
        // resolve. `selectCancellableRun` keys on RUNNING + a traceId, so without this the
        // Cancel affordance appears and points at an already-finished trace. Cleared by
        // TRACE_REALLOCATED once the real trace lands (and by every terminal transition).
        ...(action.reforecastAt ? { awaitingTrace: true } : {}),
      });
    case ACTIONS.TRACE_ALLOCATED:
      return stampRun(state, action.runId, {
        traceId: action.traceId,
        forecastId: action.forecastId,
        backend: action.backend,
      });
    case ACTIONS.TRACE_REALLOCATED: {
      const patch: Partial<Run> = {};
      if (action.traceId != null) patch.traceId = action.traceId;
      if (action.forecastId != null) patch.forecastId = action.forecastId;
      if (action.backend != null) patch.backend = action.backend;
      // Return the same state ref on an all-null payload, so a vacuous dispatch can't
      // churn selector caches or trigger a rerender.
      if (Object.keys(patch).length === 0) return state;
      if (action.traceId != null) patch.awaitingTrace = false;
      return patchRun(state, action.runId, patch);
    }
    case ACTIONS.RUN_PROGRESS: {
      const cur = runOf(state, action.runId)?.progress ?? null;
      const curIdx = cur?.stepIdx ?? -1;
      const newIdx = action.stepIdx ?? -1;
      // An unknown phase (newIdx < 0 — an unrecognized/legacy stage, e.g. a resume
      // frame carrying only "research") must NEVER blank a shown line or reset a
      // known step, which would also defeat the monotonic guard for later frames.
      if (newIdx < 0) {
        if (!cur) {
          if (!action.message) return state;
          return patchRun(state, action.runId, {
            progress: {
              stage: action.stage,
              step: action.step ?? null,
              stepIdx: -1,
              pct: action.pct ?? null,
              message: action.message,
            },
          });
        }
        if (action.message && action.message !== cur.message) {
          return patchRun(state, action.runId, { progress: { ...cur, message: action.message } });
        }
        return state;
      }
      // Equal idx is kept so a same-step tick (e.g. Researching's round counter) still updates.
      if (curIdx >= 0 && newIdx < curIdx) return state;
      return patchRun(state, action.runId, {
        progress: {
          stage: action.stage,
          step: action.step ?? null,
          stepIdx: newIdx,
          pct: action.pct ?? null,
          message: action.message ?? null,
        },
      });
    }
    case ACTIONS.RUN_UPDATE_APPLIED: {
      const run = runOf(state, action.runId);
      if (!run || isTerminal(run.status) || run.progressDoc?.outcome != null) return state;
      const update = action.update;
      const foldedProgressDoc = applyUpdate(run.progressDoc ?? EMPTY_FORECAST, update);
      const progressDoc =
        foldedProgressDoc.activity.length > MAX_RETAINED_PROGRESS_ENTRIES ||
        foldedProgressDoc.unknown.length > MAX_RETAINED_PROGRESS_ENTRIES
          ? {
              ...foldedProgressDoc,
              activity: foldedProgressDoc.activity.slice(-MAX_RETAINED_PROGRESS_ENTRIES),
              unknown: foldedProgressDoc.unknown.slice(-MAX_RETAINED_PROGRESS_ENTRIES),
            }
          : foldedProgressDoc;
      const patch: Partial<Run> = { progressDoc };
      if (isUnknownUpdate(update)) return patchRun(state, action.runId, patch);

      if (update.kind === "forecast_updated") {
        patch.progressForecastRevisions = run.progressForecastRevisions + 1;
        patch.progressSeed = run.progressSeedArmed;
        patch.progressSeedArmed = false;
        patch.progressSeedReportArmed = run.progressSeedArmed;
        // Read the seed's number off the FOLDED doc while it is still the
        // seed's; the tail revision overwrites `doc.forecast` in place.
        if (run.progressSeedArmed && run.progressSeedOutcome) {
          patch.progressSeedOutcome = {
            ...run.progressSeedOutcome,
            headline: seedHeadline(progressDoc),
          };
        }
      } else if (update.kind === "report_updated") {
        patch.progressReportRevisions = run.progressReportRevisions + 1;
        patch.progressSeedReportArmed = false;
        if (run.progressSeedReportArmed) {
          patch.progressSeedReportSeen = true;
          // Same reason as the headline above: `doc.report` becomes the tail
          // report later, so the seed's prose is only readable right here.
          if (run.progressSeedOutcome) {
            patch.progressSeedOutcome = {
              ...(patch.progressSeedOutcome ?? run.progressSeedOutcome),
              summary:
                typeof progressDoc.report?.summary === "string" ? progressDoc.report.summary : null,
            };
          }
        } else {
          patch.progressTailReport = true;
        }
      } else if (update.kind === "tool_call_started") {
        // A newly started tool is an unambiguous phase boundary: if the seed omitted
        // its estimate or report, the next one belongs to the tail. In-place updates
        // to the existing seed call may still arrive before its report and do not
        // disarm it.
        patch.progressSeedArmed = false;
        patch.progressSeedReportArmed = false;
        const startedDetail = (update.detail ?? {}) as Record<string, unknown>;
        if (isSeedCall(update.call_id)) patch.progressSeedStarted = true;
        patch.progressActivity = {
          callId: update.call_id,
          tool: update.tool ?? null,
          title: update.title ?? null,
          detail: startedDetail,
          seq: update.seq,
        };
      } else if (update.kind === "tool_call_updated") {
        const current = run.progressActivity;
        const detail = (update.detail ?? {}) as Record<string, unknown>;
        if (current && current.callId === update.call_id) {
          patch.progressActivity = {
            ...current,
            title: update.title ?? current.title,
            detail: { ...current.detail, ...detail },
            seq: update.seq,
          };
        }
      } else if (update.kind === "tool_call_finished") {
        const current = run.progressActivity;
        const detail = (update.detail ?? {}) as Record<string, unknown>;
        if (current && current.callId === update.call_id) {
          patch.progressActivity = {
            ...current,
            title: update.title ?? current.title,
            detail: { ...current.detail, ...detail },
            seq: update.seq,
            ok: update.ok,
          };
        }
        if (
          current?.callId === "seed" &&
          update.call_id === "seed" &&
          update.ok &&
          detail.seed === true
        ) {
          patch.progressSeedArmed = true;
        }
        // Open the outcome the moment the seed returns, `ok` either way: the
        // seedless fallback finishes with ok=false and sends NO forecast
        // or report after it, so a consumer that waited for those would show
        // the seed as still running for the rest of the run.
        if (isSeedCall(update.call_id)) {
          patch.progressSeedStarted = true;
          patch.progressSeedOutcome = {
            ok: update.ok === true,
            headline: run.progressSeedOutcome?.headline ?? null,
            summary: run.progressSeedOutcome?.summary ?? null,
          };
        }
      }
      const research = run.progressResearch ?? EMPTY_RESEARCH;
      const folded = foldResearch(research, update);
      if (folded !== research) patch.progressResearch = folded;
      return patchRun(state, action.runId, patch);
    }
    case ACTIONS.RUN_NARRATION_APPENDED: {
      const run = runOf(state, action.runId);
      if (!run || isTerminal(run.status)) return state;
      const beat = action.beat;
      // A replayed beat must not duplicate a chat message. Ordinals are
      // monotonic per stream, so the newest one is the only one worth checking.
      const last = run.narration[run.narration.length - 1];
      if (last && beat.ordinal <= last.ordinal) return state;
      const narration = [...run.narration, beat].slice(-MAX_RETAINED_NARRATION_BEATS);
      return patchRun(state, action.runId, { narration });
    }
    case ACTIONS.RUN_COMPLETED: {
      const completed = {
        status: STATUS.COMPLETED,
        error: null,
        awaitingTrace: false,
        contract: action.contract,
        events: action.events,
        serverContext: action.serverContext,
        // A completed result replaces the prior result authority. Keep a distinct
        // persisted headline only when this result supplied a bounded one; otherwise
        // clear it so readers fall back to THIS result's contract, never the old row.
        serverPrediction: boundedResultHeadline(action.serverPrediction),
        ...(action.forecastId != null ? { forecastId: action.forecastId } : {}),
        ...(action.partial !== undefined ? { partial: action.partial } : {}),
        ...(action.debug !== undefined ? { debug: action.debug } : {}),
        ...(action.obs !== undefined ? { obs: action.obs } : {}),
        ...(action.serverExplanation !== undefined
          ? { serverExplanation: action.serverExplanation }
          : {}),
        ...(action.report !== undefined ? { report: action.report } : {}),
        ...(action.lastReforecastedAt !== undefined
          ? { lastReforecastedAt: action.lastReforecastedAt }
          : {}),
      };
      // Record what this run now OWNS, and deliberately leave `detailPending` alone: a
      // completion does not write every blob (notably `history`), so detail IS still owed
      // and the flag is what the lean-run guards and the persister's park rule read. The
      // backfill skips the owned fields and clears the flag when it lands.
      // See docs/2026-09-17-backfill-clobbers-completion-design.md.
      return patchRun(state, action.runId, {
        ...completed,
        detailLocal: true,
        detailLocalFields: ownedAfter(state, action.runId, completed),
      });
    }
    case ACTIONS.RUN_ERRORED:
      return patchRun(state, action.runId, {
        status: STATUS.ERRORED,
        error: errorToString(action.error),
        errorCode: errorCodeOf(action.error),
        awaitingTrace: false,
      });
    case ACTIONS.RUN_CANCELLED:
      return patchRun(state, action.runId, {
        status: STATUS.CANCELLED,
        error: null,
        errorCode: null,
        awaitingTrace: false,
      });
    case ACTIONS.RUN_REMOVED: {
      // Check BOTH slices: a collaborator may clear a BRANCH of a forecast shared with
      // them, and that branch lives in `sharedRunsById`.
      const key = sliceOf(state, action.runId);
      if (!key) return state;
      const removed = state[key][action.runId]!;
      const { [action.runId]: _removed, ...rest } = state[key];
      // Root runs own their conversation (conversationId === runId). Clean up the
      // conversation entry when the root is deleted so the store doesn't accumulate
      // orphaned conversation objects from deleted forecasts across the session.
      // Branch runs share their root's conversation — leave those entries intact
      // (still reachable by sibling branches until the root is cleared).
      const isConvOwner = removed.conversationId === action.runId;
      const conversationsById = isConvOwner
        ? (({ [action.runId]: _c, ...cvRest }) => cvRest)(state.conversationsById)
        : state.conversationsById;
      // Nothing keeps naming a run that is gone. The UI already showed no selection for it
      // (`selectActiveRun` resolves a missing run to null), but the dangling id still steered
      // whatever read it raw — a chat line into a conversation nothing renders, a cancel at a
      // run that no longer exists. `comparison` keeps its reference when untouched, since
      // effects depend on it.
      const { rootIds } = state.comparison;
      return {
        ...state,
        [key]: rest,
        conversationsById,
        activeRunId: state.activeRunId === action.runId ? null : state.activeRunId,
        comparison: rootIds.includes(action.runId)
          ? { rootIds: rootIds.filter((id) => id !== action.runId) }
          : state.comparison,
        customEventPopovers: withoutPopover(state.customEventPopovers, action.runId),
      };
    }
    case ACTIONS.DELETE_REQUESTED: {
      if (state.pendingDeleteIds.includes(action.runId)) return state;
      return { ...state, pendingDeleteIds: [...state.pendingDeleteIds, action.runId] };
    }
    case ACTIONS.DELETE_ACKED: {
      if (!state.pendingDeleteIds.includes(action.runId)) return state;
      return {
        ...state,
        pendingDeleteIds: state.pendingDeleteIds.filter((id) => id !== action.runId),
      };
    }
    case ACTIONS.ENTRY_APPENDED: {
      const conv = state.conversationsById[action.conversationId] || { entries: [] };
      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.conversationId]: { ...conv, entries: [...conv.entries, action.entry] },
        },
      };
    }
    case ACTIONS.ENTRIES_LOADED: {
      const conv = state.conversationsById[action.conversationId] || { entries: [] };
      // Copy the array (and normalize non-arrays to []) so a caller mutating or
      // reusing the input can't reach into reducer state out-of-band.
      const entries = Array.isArray(action.entries) ? [...action.entries] : [];
      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.conversationId]: { ...conv, entries },
        },
      };
    }
    case ACTIONS.DRAFT_ADOPTED: {
      const draft = state.conversationsById[DRAFT_CONV_ID] || { entries: [] };
      const adoptedDraftMsgs = { ...state.adoptedDraftMsgs };
      for (const e of draft.entries) {
        const msgId = e.metadata?.msg_id;
        if (typeof msgId === "string" && msgId) adoptedDraftMsgs[msgId] = action.runId;
      }
      return {
        ...state,
        conversationsById: {
          ...state.conversationsById,
          [action.runId]: {
            ...state.conversationsById[action.runId],
            entries: [...draft.entries],
          },
          [DRAFT_CONV_ID]: { ...draft, entries: [] },
        },
        adoptedDraftMsgs,
      };
    }
    case ACTIONS.ACTIVE_SET:
      return { ...state, activeRunId: action.runId };
    case ACTIONS.COMPARISON_SET:
      return { ...state, comparison: { rootIds: [...action.rootIds] } };
    case ACTIONS.COMPARISON_CLEARED:
      return { ...state, comparison: { rootIds: [] } };
    case ACTIONS.HYDRATED: {
      const runsById = overlayRuns(state.runsById, action.runsById);
      const mergedConvs = { ...state.conversationsById, ...action.conversationsById };
      // The draft conversation is always kept (it's run-less by design).
      // A conversation is "owned" when some run references it as its conversationId —
      // NOT when cid itself is a runId, since conversationsById is keyed by conversationId
      // (a root's runId; branches share the root's).
      const ownedConvIds = new Set(Object.values(runsById).map((r) => r.conversationId));
      const conversationsById: Record<string, Conversation> = {};
      for (const [cid, conv] of Object.entries(mergedConvs)) {
        const hasRun = ownedConvIds.has(cid);
        const hasEntries = (conv?.entries?.length ?? 0) > 0;
        if (cid === DRAFT_CONV_ID || hasRun || hasEntries) conversationsById[cid] = conv;
      }
      return { ...state, runsById, conversationsById };
    }
    case ACTIONS.DETAIL_HYDRATED: {
      // Patch-in-place, never insert: a runId absent from the store was removed
      // (deleted, orphan-healed) between the lean load and the backfill, and
      // re-adding it here would resurrect a row the user just dismissed.
      // Each run is patched in the slice it lives in: a shared forecast is lean too until opened.
      const next = { runsById: state.runsById, sharedRunsById: state.sharedRunsById };
      for (const [runId, detail] of Object.entries(action.detailByRunId)) {
        const key = sliceOf(state, runId);
        if (!key) continue;
        const run = next[key][runId];
        // Fill in detail that is MISSING; never overwrite detail the run already has. A run
        // that completed locally inside the boot window holds the user's fresh result, and
        // this response was serialized before it existed — patching would silently revert it.
        // Skipping the whole ROW would strand the blobs the completion did NOT write (its
        // `history`, typically), leaving the timeline marks missing until the next reload.
        // So drop only the fields it owns and fill the rest.
        // The headline is the completion's too, though it is not one of the tracked blobs.
        const owned = [
          ...(Array.isArray(run.detailLocalFields) ? run.detailLocalFields : []),
          ...(run.detailLocal ? ["serverPrediction"] : []),
        ];
        const fill = owned.length
          ? Object.fromEntries(Object.entries(detail).filter(([k]) => !owned.includes(k)))
          : detail;
        if (next[key] === state[key]) next[key] = { ...state[key] };
        next[key][runId] = settledRun(withCopyBackend({ ...run, ...fill, detailPending: false }));
      }
      // Same reference when nothing matched, so a no-op backfill can't churn
      // every memoized selector/component subscribed to the store.
      return next.runsById === state.runsById && next.sharedRunsById === state.sharedRunsById
        ? state
        : { ...state, ...next };
    }
    case ACTIONS.DETAIL_REFRESHED: {
      let next = state;
      for (const [runId, detail] of Object.entries(action.detailByRunId)) {
        const key = sliceOf(next, runId);
        if (!key) continue;
        const history = (detail as Partial<Run>).history;
        const last = Array.isArray(history) && history.length ? history[history.length - 1] : null;
        const run = {
          ...next[key][runId],
          ...detail,
          // The refreshed vintage's moment, so a promotion already in flight for an older fire
          // is refused (shouldApplyNewestReforecast).
          lastReforecastedAt: (last as any)?.snapshotAt ?? next[key][runId].lastReforecastedAt,
          detailPending: false,
          detailLocal: false,
          detailLocalFields: undefined,
        };
        next = { ...next, [key]: { ...next[key], [runId]: settledRun(withCopyBackend(run)) } };
      }
      return next;
    }
    case ACTIONS.SHARED_HYDRATED:
      return { ...state, sharedRunsById: overlayRuns(state.sharedRunsById, action.runsById) };
    case ACTIONS.SHARED_ACCESS_LOST: {
      // Same reference when the row is already gone (a double-click racing two
      // 404s is the ordinary case here), so a repeat cannot churn every memoized
      // selector subscribed to the store. The lost row leaves `activeRunId`, the
      // comparison lanes and its popover the way a removed owned run does
      // (`RUN_REMOVED` above): a dangling id would steer raw readers — unless the
      // owner slice holds the same id, which wins a collision and keeps its UI state.
      if (!state.sharedRunsById[action.runId]) return state;
      const { [action.runId]: _gone, ...rest } = state.sharedRunsById;
      if (state.runsById[action.runId]) return { ...state, sharedRunsById: rest };
      const { rootIds } = state.comparison;
      return {
        ...state,
        sharedRunsById: rest,
        activeRunId: state.activeRunId === action.runId ? null : state.activeRunId,
        comparison: rootIds.includes(action.runId)
          ? { rootIds: rootIds.filter((id) => id !== action.runId) }
          : state.comparison,
        customEventPopovers: withoutPopover(state.customEventPopovers, action.runId),
      };
    }
    case ACTIONS.SHARED_RESET:
      return state.sharedRunsById === initialState.sharedRunsById
        ? state
        : { ...state, sharedRunsById: {} };
    case ACTIONS.SCHEDULE_SET:
      return patchRun(state, action.runId, { schedule: action.schedule });
    case ACTIONS.SAYER_COPY_ADOPTED: {
      // Only what the conversion changed. A full hydrate of the converted row would roll back a
      // newer forecast the copy picked up but has not saved yet (its headline and pickup time),
      // and could not clear the marker from a context the pickup holds locally.
      const run = runOf(state, action.runId);
      if (!run) return state;
      const ctx: any = run.serverContext;
      let serverContext = ctx;
      if (ctx?.origin) {
        const { origin, ...rest } = ctx;
        serverContext = {
          ...rest,
          // Kept, as the server keeps it, so the copy holds its place in the list.
          ...(origin.openedAt && !rest.openedAt ? { openedAt: origin.openedAt } : {}),
        };
      }
      const set = Object.fromEntries(
        Object.entries(action.converted).filter(([, v]) => v !== undefined),
      );
      return patchRun(state, action.runId, {
        ...set,
        serverContext,
        // A detail backfill serialized before the conversion must not restore the marker. Only
        // the owned set, not `detailLocal`, which would make the persister re-POST the row.
        detailLocalFields: ownedAfter(state, action.runId, { serverContext }),
      } as Partial<Run>);
    }
    case ACTIONS.NOTIFY_PATCHED: {
      const held = runOf(state, action.runId);
      if (!held) return state;
      return patchRun(state, action.runId, { notify: { ...held.notify, ...action.patch } });
    }
    case ACTIONS.SERIES_SET:
      return patchRun(state, action.runId, { series: action.series });
    case ACTIONS.RESOLUTION_SET: {
      // A void is final: a resolver poll that was in flight when the void landed must not
      // replace it with the verdict it went on to read. Only another void may.
      const held = runOf(state, action.runId);
      if (held && isVoided(held) && !isVoided({ resolution: action.resolution })) return state;
      return settleRun(
        patchRun(state, action.runId, { resolution: action.resolution }),
        action.runId,
      );
    }
    case ACTIONS.PREMISE_CLEARED:
      return patchRun(state, action.runId, { premise: action.premise });
    case ACTIONS.PREMISE_OUTCOMES_MARKED:
      return patchRun(state, action.runId, { events: action.events });
    case ACTIONS.BRANCH_ARCHIVED:
      return patchRun(state, action.runId, { clearedAt: action.clearedAt });
    case ACTIONS.CONTRACT_RESOLVED:
      return settleRun(
        patchRun(state, action.runId, {
          ...(action.contractStatus !== undefined ? { contractStatus: action.contractStatus } : {}),
          ...(action.resolution !== undefined ? { resolution: action.resolution } : {}),
        }),
        action.runId,
      );
    case ACTIONS.ALERT_SET:
      return patchRun(state, action.runId, { alert: action.alert });
    case ACTIONS.SERIES_IMPACTS_SET:
      return patchRun(state, action.runId, { seriesImpacts: action.seriesImpacts });
    case ACTIONS.FORK_EVENT_SET:
      return patchRun(state, action.runId, { forkEvent: action.forkEvent });
    case ACTIONS.CUSTOM_EVENT_POPOVER_OPENED:
      if (!sliceOf(state, action.runId)) return state;
      return {
        ...state,
        customEventPopovers: { ...state.customEventPopovers, [action.runId]: action.popover },
      };
    case ACTIONS.CUSTOM_EVENT_POPOVER_EDITED: {
      const open = state.customEventPopovers?.[action.runId];
      if (!open) return state;
      return {
        ...state,
        customEventPopovers: {
          ...state.customEventPopovers,
          [action.runId]: { ...open, ...action.patch },
        },
      };
    }
    case ACTIONS.CUSTOM_EVENT_POPOVER_REJECTED:
      if (!sliceOf(state, action.runId) || state.customEventPopovers?.[action.runId]) return state;
      return {
        ...state,
        customEventPopovers: { ...state.customEventPopovers, [action.runId]: action.popover },
      };
    case ACTIONS.CUSTOM_EVENT_POPOVER_CLOSED: {
      const next = withoutPopover(state.customEventPopovers, action.runId);
      return next === state.customEventPopovers ? state : { ...state, customEventPopovers: next };
    }
    case ACTIONS.DERIVED_TRIGGERS_ATTACHED: {
      const run = runOf(state, action.runId);
      if (!run || !run.forkDerived || run.forecastId !== action.forecastId) return state;
      if (Array.isArray(run.events) && run.events.length > 0) return state;
      return patchRun(state, action.runId, { events: action.events });
    }
    case ACTIONS.BRANCH_LINK_FOCUSED: {
      if (!runOf(state, action.runId)) return state;
      // Expand the branch and every parent branch up to the root, so the linked lane is drawn.
      let next = state;
      const seen = new Set<string>();
      for (
        let run = runOf(state, action.runId);
        run && run.parentRunId != null && !seen.has(run.runId);
        run = runOf(state, run.parentRunId)
      ) {
        seen.add(run.runId);
        if (run.collapsed) next = patchRun(next, run.runId, { collapsed: false });
      }
      const seq = (state.branchLinkFocus?.seq ?? 0) + 1;
      return { ...next, branchLinkFocus: { runId: action.runId, seq } };
    }
    case ACTIONS.BRANCH_COLLAPSE_TOGGLED: {
      const run = runOf(state, action.runId);
      if (!run) return state;
      return patchRun(state, action.runId, { collapsed: !run.collapsed });
    }
    case ACTIONS.RUN_REFORECASTED: {
      const reforecasted = {
        // Explicit terminal status: a reforecast lands a fresh completed result, so the run
        // stays COMPLETED rather than relying on the call-site having left it COMPLETED.
        status: STATUS.COMPLETED,
        error: null,
        contract: action.contract,
        events: action.events,
        serverContext: keepSayerCopyContext(
          runOf(state, action.runId)?.serverContext,
          action.serverContext,
        ),
        serverPrediction: boundedResultHeadline(action.serverPrediction),
        lastReforecastedAt: action.lastReforecastedAt,
        ...(action.partial !== undefined ? { partial: action.partial } : {}),
        ...(action.debug !== undefined ? { debug: action.debug } : {}),
        ...(action.obs !== undefined ? { obs: action.obs } : {}),
        ...(action.serverExplanation !== undefined
          ? { serverExplanation: action.serverExplanation }
          : {}),
        ...(action.report !== undefined ? { report: action.report } : {}),
        ...(action.history !== undefined ? { history: action.history } : {}),
        ...(action.superseded !== undefined ? { superseded: action.superseded } : {}),
        // Sync the pointer list with the promoted state: the report modal's LIVE
        // overlay resolves the LATEST report-pointer, so a promote that left this
        // stale would show the pre-arrival prediction while the timeline shows the
        // new one.
        //
        // UNIONED, not assigned. The list rides this action from a single response, and a
        // response serialized from an older snapshot can promote AFTER a fuller one has
        // already been merged by `RUN_POINTERS_MERGED` — assigning would clobber that union.
        ...(action.reportPointers !== undefined
          ? {
              reportPointers: mergeReportPointers(
                runOf(state, action.runId)?.reportPointers,
                action.reportPointers,
              ),
            }
          : {}),
      };
      // Same ownership rule as RUN_COMPLETED, and `detailPending` is likewise untouched. A
      // reforecast may also write `history`, which is why the owned set is derived from the
      // patch rather than hand-listed.
      const backend = withCopyBackend({
        backend: runOf(state, action.runId)?.backend,
        serverContext: reforecasted.serverContext,
      }).backend;
      return settleRun(
        patchRun(state, action.runId, {
          ...reforecasted,
          ...(backend != null ? { backend: backend as string } : {}),
          detailLocal: true,
          detailLocalFields: ownedAfter(state, action.runId, reforecasted),
        }),
        action.runId,
      );
    }
    case ACTIONS.RUN_POINTERS_MERGED: {
      // Metadata only — no status, no result fields, no detail ownership — and permitted on
      // a RUNNING run, which is the ordinary case rather than an edge one. A merge that adds
      // nothing returns the same state reference, so a redelivered hint cannot churn every
      // selector subscribed to the store.
      const run = runOf(state, action.runId);
      if (!run) return state;
      const merged = unionReportPointers(run.reportPointers, action.reportPointers);
      return merged
        ? settleRun(patchRun(state, action.runId, { reportPointers: merged }), action.runId)
        : state;
    }
    default:
      return state;
  }
}
